import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { buildPostLoginPath, getStoredAuth } from "./auth";
import { useSSE } from "./hooks/useSSE";
import { useTheme } from "./hooks/useTheme";
import { useOnboarding } from "./hooks/useOnboarding";
import Login from "./pages/Login";
import Register from "./pages/Register";
import WorkspaceShell from "./workspace/WorkspaceShell";
import SettingsHub from "./workspace/SettingsHub";
import ShellLayout from "./ShellLayout";
import PlatformAdminBanner from "./PlatformAdminBanner";
import { resolveLegacyAdminPath } from "./workspace/settingsCatalog";
import {
  LazyAgentCapabilities,
  LazyAgentEmail,
  LazyAgentWallet,
  LazyApprovalQueue,
  LazyAreas,
  LazyAudit,
  LazyAutonomyTelemetry,
  LazyBrowserAccess,
  LazyCanvas,
  LazyChallengeQueue,
  LazyCommsPage,
  LazyConfigEditor,
  LazyContextFiles,
  LazyContextInspector,
  LazyCredentials,
  LazyDashboard,
  LazyDataSources,
  LazyDecisionLog,
  LazyDevLoop,
  LazyEmailDrafts,
  LazyInbox,
  LazyInjectionDefense,
  LazyMandates,
  LazyMiddlewareConfig,
  LazyModels,
  LazyOperatorModel,
  LazyOptimizer,
  LazyPluginManager,
  LazyProjects,
  LazyRateLimits,
  LazyRelationshipGraph,
  LazyRetrospective,
  LazySecurityObservability,
  LazyServices,
  LazySetupWizard,
  LazySystemHealth,
  LazyToday,
  LazyUpcoming,
  LazyWorkspaceAccess,
  LazyWorkspaceZones,
  withSuspense,
} from "./routes";

function LegacyAdminRedirect(): React.ReactElement {
  const location = useLocation();
  const target = resolveLegacyAdminPath(location.pathname);
  return <Navigate to={target ?? "/"} replace />;
}

export default function App(): React.ReactElement {
  const location = useLocation();
  const authState = getStoredAuth();
  const authRequired = import.meta.env.PROD || import.meta.env.VITE_HANK_REQUIRE_AUTH === "true";
  const isAuthenticated = !!authState.token;
  const isPublicAuthPath = location.pathname === "/login" || location.pathname === "/register";
  const { connected, events } = useSSE("/api/events", {
    enabled: !authRequired || isAuthenticated,
    authToken: authState.token,
  });
  const { theme, toggle: toggleTheme } = useTheme();
  const {
    needsOnboarding,
    loading: onboardingLoading,
    blocker: onboardingBlocker,
  } = useOnboarding(!authRequired || (isAuthenticated && !isPublicAuthPath));

  if (authRequired) {
    if (isPublicAuthPath) {
      if (isAuthenticated) {
        return (
          <Routes>
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
          </Routes>
        );
      }
      return (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Routes>
      );
    }

    if (!isAuthenticated) {
      return (
        <Routes>
          <Route
            path="*"
            element={
              <Navigate to="/login" replace state={{ from: buildPostLoginPath(location) }} />
            }
          />
        </Routes>
      );
    }
  }

  return (
    <>
      {/* F-014 (session 3): platform_admin sessions get an explanatory banner
          since tenant-scoped APIs return 403 in this context. Renders above
          every authenticated route. Null for tenant_owner / other roles. */}
      <PlatformAdminBanner />
      <Routes>
        <Route path="/onboarding" element={<Navigate to="/" replace />} />
        <Route path="/settings" element={<SettingsHub />} />
        <Route path="/settings/:sectionId" element={<SettingsHub />} />
        <Route path="/settings/:sectionId/:pageId" element={<SettingsHub />} />

        {/* Standalone surfaces — wrapped in ShellLayout so each has the
          back-to-workspace shortcut and the global Cmd+K palette. The
          `/admin/<page>` routes MUST appear before the legacy
          `/admin/*` redirect catch-all so React Router v6 picks the
          more-specific match. */}
        <Route element={<ShellLayout />}>
          {/* Phase 3 organizer surfaces */}
          <Route path="/today" element={withSuspense(<LazyToday />)} />
          <Route path="/inbox" element={withSuspense(<LazyInbox />)} />
          <Route path="/upcoming" element={withSuspense(<LazyUpcoming />)} />
          <Route path="/areas" element={withSuspense(<LazyAreas />)} />
          <Route path="/projects" element={withSuspense(<LazyProjects />)} />
          <Route path="/comms" element={withSuspense(<LazyCommsPage />)} />
          <Route path="/canvas" element={withSuspense(<LazyCanvas />)} />

          {/* Trust / transparency surfaces */}
          <Route path="/transparency/decisions" element={withSuspense(<LazyDecisionLog />)} />
          <Route path="/transparency/retrospective" element={withSuspense(<LazyRetrospective />)} />
          <Route path="/transparency/autonomy" element={withSuspense(<LazyAutonomyTelemetry />)} />
          <Route
            path="/transparency/operator-model"
            element={withSuspense(<LazyOperatorModel />)}
          />
          {/* R1.10 (LA-022 / LA-024) — Context Inspector: per-session
            token-budget observability for the assembled prependContext
            stack. */}
          <Route
            path="/transparency/context-inspector"
            element={withSuspense(<LazyContextInspector />)}
          />

          {/* Admin / operator-secondary pages.  Paths intentionally avoid
            the legacy alias map in settingsCatalog.tsx so the redirect
            catch-all below still resolves /admin/approvals,
            /admin/capabilities, etc. to /settings as before. */}
          <Route path="/admin/dashboard" element={withSuspense(<LazyDashboard />)} />
          <Route path="/admin/approval-queue" element={withSuspense(<LazyApprovalQueue />)} />
          <Route path="/admin/challenge-queue" element={withSuspense(<LazyChallengeQueue />)} />
          <Route
            path="/admin/agent-capabilities"
            element={withSuspense(<LazyAgentCapabilities />)}
          />
          <Route path="/admin/agent-email-page" element={withSuspense(<LazyAgentEmail />)} />
          <Route path="/admin/agent-wallet" element={withSuspense(<LazyAgentWallet />)} />
          <Route path="/admin/audit" element={withSuspense(<LazyAudit />)} />
          <Route path="/admin/browser-access" element={withSuspense(<LazyBrowserAccess />)} />
          <Route path="/admin/config-editor" element={withSuspense(<LazyConfigEditor />)} />
          <Route path="/admin/context-files" element={withSuspense(<LazyContextFiles />)} />
          <Route path="/admin/credentials" element={withSuspense(<LazyCredentials />)} />
          <Route path="/admin/data-sources-page" element={withSuspense(<LazyDataSources />)} />
          <Route path="/admin/dev-loop" element={withSuspense(<LazyDevLoop />)} />
          <Route path="/admin/email-drafts" element={withSuspense(<LazyEmailDrafts />)} />
          <Route path="/admin/injection-defense" element={withSuspense(<LazyInjectionDefense />)} />
          <Route path="/admin/mandates" element={withSuspense(<LazyMandates />)} />
          <Route path="/admin/middleware-config" element={withSuspense(<LazyMiddlewareConfig />)} />
          <Route path="/admin/models" element={withSuspense(<LazyModels />)} />
          <Route path="/admin/optimizer" element={withSuspense(<LazyOptimizer />)} />
          <Route path="/admin/plugin-manager" element={withSuspense(<LazyPluginManager />)} />
          <Route path="/admin/rate-limits" element={withSuspense(<LazyRateLimits />)} />
          <Route
            path="/admin/relationship-graph"
            element={withSuspense(<LazyRelationshipGraph />)}
          />
          <Route
            path="/admin/security-observability"
            element={withSuspense(<LazySecurityObservability />)}
          />
          <Route path="/admin/services" element={withSuspense(<LazyServices />)} />
          <Route path="/admin/system-health" element={withSuspense(<LazySystemHealth />)} />
          <Route path="/admin/setup-wizard" element={withSuspense(<LazySetupWizard />)} />
          <Route
            path="/admin/workspace-access-page"
            element={withSuspense(<LazyWorkspaceAccess />)}
          />
          <Route path="/admin/workspace-zones" element={withSuspense(<LazyWorkspaceZones />)} />
        </Route>

        {/* Legacy admin alias catch-all — runs only when no explicit
          /admin/<page> route above matched. Preserves the
          settingsCatalog.tsx redirect map (e.g. /admin/integrations
          → /settings/connect/ai). */}
        <Route path="/admin/*" element={<LegacyAdminRedirect />} />

        <Route
          path="*"
          element={
            <WorkspaceShell
              sseConnected={connected}
              notificationCount={events.length}
              sseEvents={events}
              theme={theme}
              onThemeToggle={toggleTheme}
              needsOnboarding={!onboardingLoading && needsOnboarding}
              onboardingBlocker={onboardingBlocker}
            />
          }
        />
      </Routes>
    </>
  );
}
